import { FC } from 'react';

import dayjs from 'dayjs';

import { useCircleInquiryQuery } from '@queries/UserSocialCircleQueries';

import { useNavigate } from '@router/index';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { Screen } from '@components/layout/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

interface SocialCircleInquiryScreenProps {
  socialCircleInquiryId: string;
  back?: string;
}

export const SocialCircleInquiryScreen: FC<SocialCircleInquiryScreenProps> = ({
  socialCircleInquiryId,
  back,
}) => {
  const navigate = useNavigate();
  const { inquiryIsReady, inquiry } = useCircleInquiryQuery(socialCircleInquiryId);

  const goingCount = Object.values(inquiry?.responses ?? {}).filter(
    (r) => r.rsvpStatus === 'yes',
  ).length;
  const maybeCount = Object.values(inquiry?.responses ?? {}).filter(
    (r) => r.rsvpStatus === 'maybe',
  ).length;
  const notGoingCount = Object.values(inquiry?.responses ?? {}).filter(
    (r) => r.rsvpStatus === 'no',
  ).length;

  const handleViewMessages = () => {
    navigate(`/chat/conversation/${inquiry?.conversationId}`);
  };

  return (
    <Screen
      name="Social Circle Inquiry Screen"
      headerProps={{
        title: inquiry?.circle?.name ?? 'Circle Inquiry',
      }}
    >
      <Screen.Content>
        {!inquiryIsReady ? (
          <>
            <div className="mb-3">
              <Skeleton height={120} />
            </div>
            <Skeleton height={80} />
          </>
        ) : (
          <>
            <TitleToolbar text={inquiry?.title ?? 'Untitled Inquiry'} className="mb-0" />
            <TitleToolbar
              text={dayjs(inquiry?.eventDate).format('MMM D, YYYY')}
              className="mt-0"
              size="sm"
            />

            <div className="d-flex gap-2 mb-2 w-100">
              <div className="w-50">
                <Card>
                  <Card.Body>
                    <div className="text-center">
                      <div className="fw-bold">{goingCount}</div>
                      <div className="small">Going</div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="w-50">
                <Card>
                  <Card.Body>
                    <div className="text-center">
                      <div className="fw-bold">{maybeCount}</div>
                      <div className="small">Maybe</div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>

            <Card>
              <Card.Body>
                <p className="mb-0">
                  <em>"{inquiry?.message}"</em>
                </p>
              </Card.Body>
              <Card.Footer>
                <Button
                  variant="smooth"
                  color="neutral"
                  roundness="rounded"
                  onClick={handleViewMessages}
                >
                  View Messages
                </Button>
              </Card.Footer>
            </Card>

            <div className="d-grid mt-2 gap-2 w-100">
              <Button variant="smooth" color="neutral" roundness="rounded" onClick={() => {}}>
                RSVP
              </Button>
              <Button variant="smooth" color="neutral" roundness="rounded" onClick={() => {}}>
                Invite New Member
              </Button>
              <Button variant="smooth" color="neutral" roundness="rounded" onClick={() => {}}>
                Invite One-time Guest
              </Button>
            </div>
          </>
        )}
      </Screen.Content>
    </Screen>
  );
};
